import { Button, Col, Form, Input, Row, Card, message } from 'antd';
import { UpOutlined, DownOutlined } from '@ant-design/icons';
import React, { useState, useCallback } from 'react';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { GameProfileItem } from '../../../types/gameProfile';
import { createOrUpdateGameProfileEntryCardRepository } from '../../../repository/mytm/gameProfile/gameProfileRepository';
import { logging } from '../../../utils/log';
import { CONFIG_CONTAINER_NAME, uploadFileToBlob, UploadFileToBlobInterface } from '../../../utils/azure';
import { GAME_PROFILE } from '../../../constants/define/translation';
import { PreviewEntryCard } from '../../molecules/preview/common/PreviewEntryCard';
import { DragDropImageGameProfile } from '../../molecules/upload';
import { DEFAULT_TIME, ZONE } from '../../../constants/common';

const azureCDN = process.env.REACT_APP_AZURE_CDN;
const storageAccountName = process.env.REACT_APP_AZURE_BLOB_ACCOUNT_NAME;

interface EntryCardProps {
  data?: GameProfileItem | null;
  onSave?: (data: GameProfileItem) => void;
  country?: string | null;
}

export const EntryCardEnhanced: React.FC<EntryCardProps> = ({ data, onSave, country }) => {
  const { t } = useTranslation(GAME_PROFILE);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [imageList, setImageList] = useState<UploadFileToBlobInterface[]>([]);
  const [selectedImage, setSelectedImage] = useState(0);
  const [formValues, setFormValues] = useState<any>({});
  const [startTime, setStartTime] = useState<any>(DEFAULT_TIME);
  const [endTime, setEndTime] = useState<any>(DEFAULT_TIME);

  const onSetImageList = useCallback((imageData: UploadFileToBlobInterface[]) => {
    setImageList(imageData);
  }, []);

  const handleFormChange = useCallback(() => {
    const values = form.getFieldsValue();
    setFormValues(values);
  }, [form]);

  const handleImageSelect = useCallback((index: number) => {
    setSelectedImage(index);
  }, []);

  // Initialize form values and image list
  React.useEffect(() => {
    const initialValues = {
      header: data?.header || '',
      subHeader: data?.subHeader || '',
      ctaButton: data?.ctaButton || '',
      backgroundColor: data?.backgroundColor || '',
      annexActionUsaId: data?.annexActionUsaId || '',
      annexActionCanId: data?.annexActionCanId || '',
    };
    setFormValues(initialValues);

    // Initialize image list with existing image if available
    if (data?.imageLink) {
      setImageList([{ dataURL: data.imageLink, isImageURL: true }]);
    } else {
      setImageList([]);
    }

    // Initialize time values from data - convert from UTC to local timezone for display
    if (data?.startTime) {
      // API returns UTC, convert to local timezone for display
      const startTimeMoment = moment.utc(data.startTime).tz(ZONE);
      setStartTime(startTimeMoment);
    }
    if (data?.endTime) {
      // API returns UTC, convert to local timezone for display
      const endTimeMoment = moment.utc(data.endTime).tz(ZONE);
      setEndTime(endTimeMoment);
    }
  }, [data]);

  // Set form values after form is initialized
  React.useEffect(() => {
    if (data && form) {
      const formData = {
        header: data.header || '',
        subHeader: data.subHeader || '',
        ctaButton: data.ctaButton || '',
        backgroundColor: data.backgroundColor || '',
        annexActionUsaId: data.annexActionUsaId || '',
        annexActionCanId: data.annexActionCanId || '',
        // Convert UTC to local timezone for date display - return moment objects, not strings
        startDate: data.startTime ? moment.utc(data.startTime).tz(ZONE) : null,
        endDate: data.endTime ? moment.utc(data.endTime).tz(ZONE) : null,
      };
      form.setFieldsValue(formData);
    }
  }, [data, form]);

  const handleSave = async (values: any) => {
    try {
      setLoading(true);

      // Handle image upload if there are new images
      let imageUrl = data?.imageLink || '';
      if (imageList.length > 0) {
        const imgList = imageList.filter(img => !img.isImageURL);
        const imgLink = imageList.filter(img => img.isImageURL);

        if (imgList.length > 0) {
          try {
            // Upload new images to Azure Blob Storage
            const uploadedImages = await uploadFileToBlob(imgList, CONFIG_CONTAINER_NAME.UPLOADS);
            let newImagesCDN = uploadedImages;
            if (azureCDN) {
              newImagesCDN = uploadedImages.map(url =>
                url.replace(`${storageAccountName}.blob.core.windows.net`, azureCDN),
              );
            }
            const [firstNewImage] = newImagesCDN;
            const [firstExistingImage] = imgLink.map(v => v.dataURL);
            imageUrl = newImagesCDN && newImagesCDN.length ? firstNewImage : firstExistingImage;
          } catch (uploadError) {
            message.error(t('entryCard.uploadError'));
            setLoading(false);
            return;
          }
        } else {
          const [firstImage] = imgLink.map(v => v.dataURL);
          imageUrl = firstImage;
        }
      } else {
        // No image selected, clear the imageUrl
        imageUrl = '';
      }

      // Combine date and time values - create moment objects directly
      let startDateTime = null;
      let endDateTime = null;

      if (values.startDate && startTime) {
        // Ensure values.startDate is a moment object
        const startDateMoment = moment.isMoment(values.startDate) ? values.startDate : moment(values.startDate);
        startDateTime = startDateMoment.tz(ZONE).set({
          hour: startTime.hour(),
          minute: startTime.minute(),
          second: 0,
          millisecond: 0,
        });
      }

      if (values.endDate && endTime) {
        // Ensure values.endDate is a moment object
        const endDateMoment = moment.isMoment(values.endDate) ? values.endDate : moment(values.endDate);
        endDateTime = endDateMoment.tz(ZONE).set({
          hour: endTime.hour(),
          minute: endTime.minute(),
          second: 0,
          millisecond: 0,
        });
      }

      const formData = {
        header: values.header,
        subHeader: values.subHeader,
        ctaButton: values.ctaButton,
        backgroundColor: values.backgroundColor,
        annexActionUsaId: values.annexActionUsaId,
        annexActionCanId: values.annexActionCanId,
        // Convert from local timezone to UTC for API
        startTime: startDateTime
          ? (() => {
              const utcTime = startDateTime.utc().toISOString();
              return utcTime;
            })()
          : null,
        endTime: endDateTime
          ? (() => {
              const utcTime = endDateTime.utc().toISOString();
              return utcTime;
            })()
          : null,
        imageLink: imageUrl,
        country: country || undefined,
      };

      const result = await createOrUpdateGameProfileEntryCardRepository(formData);
      onSave?.(result);
      message.success(t('entryCard.entryCardSuccess'));
    } catch (error) {
      logging(error);
      message.error(t('entryCard.entryCardFailed'));
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Card style={{ marginBottom: 24, marginTop: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 'bold' }}>{t('entryCard.title')}</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Button type="text" icon={isExpanded ? <UpOutlined /> : <DownOutlined />} onClick={toggleExpanded} />
        </div>
      </div>

      {isExpanded && (
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            header: data?.header || '',
            subHeader: data?.subHeader || '',
            ctaButton: data?.ctaButton || '',
            backgroundColor: data?.backgroundColor || '',
            annexActionUsaId: data?.annexActionUsaId || '',
            annexActionCanId: data?.annexActionCanId || '',
          }}
          onFinish={handleSave}
          onValuesChange={handleFormChange}
        >
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item
                name="header"
                label={<span style={{ fontWeight: 'bold' }}>{t('entryCard.header')}</span>}
                rules={[{ required: true, message: t('entryCard.validate.header') }]}
              >
                <Input placeholder={t('entryCard.placeholder.header')} maxLength={255} style={{ borderRadius: 6 }} />
              </Form.Item>

              <Form.Item
                name="ctaButton"
                label={<span style={{ fontWeight: 'bold' }}>{t('entryCard.buttonText')}</span>}
                rules={[{ required: true, message: t('entryCard.validate.buttonText') }]}
              >
                <Input
                  placeholder={t('entryCard.placeholder.buttonText')}
                  maxLength={255}
                  style={{ borderRadius: 6 }}
                />
              </Form.Item>

              {country === 'USA' && (
                <Form.Item
                  name="annexActionUsaId"
                  label={<span style={{ fontWeight: 'bold' }}>{t('entryCard.annexActionId')}</span>}
                >
                  <Input placeholder={t('entryCard.placeholder.id')} style={{ borderRadius: 6 }} />
                </Form.Item>
              )}
            </Col>

            <Col span={8}>
              <Form.Item
                name="subHeader"
                label={<span style={{ fontWeight: 'bold' }}>{t('entryCard.subHeader')}</span>}
              >
                <Input placeholder={t('entryCard.placeholder.subHeader')} maxLength={255} style={{ borderRadius: 6 }} />
              </Form.Item>

              <Form.Item
                name="backgroundColor"
                label={<span style={{ fontWeight: 'bold' }}>{t('entryCard.backgroundColor')}</span>}
              >
                <Input placeholder={t('entryCard.placeholder.backgroundColor')} style={{ borderRadius: 6 }} />
              </Form.Item>

              {country === 'CAN' && (
                <Form.Item
                  name="annexActionCanId"
                  label={<span style={{ fontWeight: 'bold' }}>{t('entryCard.annexActionCanId')}</span>}
                >
                  <Input placeholder={t('entryCard.placeholder.id')} style={{ borderRadius: 6 }} />
                </Form.Item>
              )}
            </Col>

            <Col span={8}>
              <div style={{ marginTop: 24 }}>
                <div style={{ height: 310, backgroundColor: '#BFBFBF', borderRadius: 8 }}>
                  <DragDropImageGameProfile
                    imageList={imageList}
                    onSetImageList={onSetImageList}
                    selectedImage={selectedImage}
                    onSelectedImage={handleImageSelect}
                    maxImage={1}
                    multipleUpload={false}
                    nodePreview={
                      <PreviewEntryCard
                        values={{
                          ...formValues,
                          backgroundImage: imageList?.length ? imageList[0]?.dataURL : null,
                        }}
                        isPreview
                        height="200px"
                      />
                    }
                  />
                </div>
              </div>
            </Col>
          </Row>

          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{ backgroundColor: '#000', borderColor: '#000', borderRadius: 6, height: 44 }}
            >
              {t('entryCard.saveButton')}
            </Button>
          </div>
        </Form>
      )}
    </Card>
  );
};
